/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ShieldCheck, ArrowRight, Mail, Store as StoreIcon } from 'lucide-react';
import PixelTracker from '@/components/PixelTracker';
import { useHideEmergentBadge } from '@/hooks/useHideEmergentBadge';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CampaignLanding() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  useHideEmergentBadge();
  const storeName = data?.store?.branding?.display_name || data?.store?.name || '';
  useDocumentTitle(storeName || 'Boutique');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/storefront/by-slug/${slug}`);
        setData(r.data);
        // Apply favicon dynamically using the store logo if any (white-label)
        if (r.data.store.branding.logo_url) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = r.data.store.branding.logo_url.startsWith('http')
            ? r.data.store.branding.logo_url
            : `${BACKEND_URL}${r.data.store.branding.logo_url}`;
        }
        document.title = r.data.store.branding.display_name || r.data.store.name;
      } catch (e) {
        setError(e?.response?.data?.detail || 'Boutique introuvable');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-zinc-400" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="p-10 max-w-md text-center">
        <h1 className="font-display text-2xl font-bold mb-2">Page introuvable</h1>
        <p className="text-zinc-600">{error}</p>
      </Card>
    </div>
  );

  const accent = data.store.branding.accent_color || '#0A0A0A';
  const logoUrl = data.store.branding.logo_url
    ? (data.store.branding.logo_url.startsWith('http') ? data.store.branding.logo_url : `${BACKEND_URL}${data.store.branding.logo_url}`)
    : '';
  const name = data.store.branding.display_name || data.store.name;

  return (
    <div className="min-h-screen bg-white">
      <PixelTracker
        storeId={data.store.id}
        slug={data.store.slug}
        event="page_view_campaign"
        metaPixelId={data.store.pixels?.meta}
        tiktokPixelId={data.store.pixels?.tiktok}
        googleTagId={data.store.pixels?.google}
        googleLabel={data.store.pixels?.google_label}
      />
      {/* Header */}
      <header className="border-b border-zinc-100 bg-white/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0" data-testid="campaign-store-brand">
            {logoUrl ? (
              <img src={logoUrl} alt={name} className="h-9 max-w-[180px] object-contain" />
            ) : (
              <>
                <div className="w-9 h-9 flex items-center justify-center rounded-md" style={{ backgroundColor: accent }}>
                  <StoreIcon className="h-4 w-4 text-white" />
                </div>
                <span className="font-display font-bold tracking-tight text-lg truncate">{name}</span>
              </>
            )}
          </div>
          <div className="text-xs text-zinc-500 flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Paiement sécurisé
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="overline mb-2" style={{ color: accent }}>— Notre sélection</div>
          <h1 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">{name}</h1>
          <p className="text-zinc-600 mt-3 text-sm lg:text-base max-w-2xl">
            Découvrez notre catalogue · paiement express · expédition rapide.
          </p>
        </div>

        {data.products.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-zinc-500">Aucun produit disponible pour le moment.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="campaign-products-grid">
            {data.products.map((p) => (
              <Card key={p.id} className="overflow-hidden border-zinc-200 hover:border-zinc-300 transition group" data-testid={`campaign-product-${p.id}`}>
                {p.image_url ? (
                  <div className="aspect-square bg-zinc-50 overflow-hidden">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  </div>
                ) : (
                  <div className="aspect-square bg-zinc-100" />
                )}
                <div className="p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold text-sm leading-tight">{p.name}</h3>
                    <div className="mt-1 text-base font-bold font-mono" style={{ color: accent }}>{p.price.toFixed(2)} €</div>
                  </div>
                  <Link to={`/checkout/${p.id}`} className="block">
                    <Button
                      className="w-full rounded-md text-white hover:opacity-90"
                      style={{ backgroundColor: accent }}
                      data-testid={`campaign-buy-${p.id}`}
                    >
                      Acheter <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-zinc-100 mt-12 py-8">
        <div className="max-w-5xl mx-auto px-6 text-xs text-zinc-500 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} {name}</div>
          {data.store.branding.support_email && (
            <a href={`mailto:${data.store.branding.support_email}`} className="flex items-center gap-1.5 hover:text-zinc-700" data-testid="campaign-support-email">
              <Mail className="h-3.5 w-3.5" /> {data.store.branding.support_email}
            </a>
          )}
        </div>
      </footer>
    </div>
  );
}
